import { isClickedOutside, lockScroll, unlockScroll } from '@theme/utilities';

/**
 * <gamba-menu-drawer> — drawer de navegación mobile.
 *
 * El diálogo es un `<dialog>` nativo abierto con `showModal()`, así que del
 * contrato de accesibilidad de un modal el motor cubre cuatro cosas sin código
 * nuestro: la trampa de foco, el fondo inerte (no tabulable, no clickeable, no
 * leído), el cierre con `Escape` y el top layer. El detalle de por qué eso es
 * mejor que un `div` con `hidden` está en el docblock de
 * `snippets/gamba-menu-drawer.liquid`.
 *
 * Lo que queda para este archivo son las cinco cosas que el motor NO hace:
 *
 *   1. `aria-expanded` del botón que abre, sincronizado con el estado real.
 *   2. Bloqueo del scroll de la página de atrás.
 *   3. Cierre al hacer click en el velo.
 *   4. Devolución del foco al botón que abrió.
 *   5. Cierre al cruzar a >=990px, donde la hamburguesa ya no existe.
 *
 * NO extiende `Component` (`assets/component.js`) a propósito, igual que
 * `gamba-rail.js`: de esa clase base sólo se usarían `refs` y los bindings
 * `on:`, y a cambio vendría un MutationObserver con `subtree: true` por
 * instancia. Este componente tiene cinco listeners y un solo hijo que le
 * importa.
 */

/** El único breakpoint del theme. Arriba de acá la hamburguesa es `display: none`. */
const DESKTOP = '(min-width: 990px)';

class GambaMenuDrawer extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** El control que abre el drawer. Vive en el header, fuera de este elemento. */
  /** @type {HTMLElement | null} */
  #toggle = null;

  /** @type {MediaQueryList} */
  #desktop = matchMedia(DESKTOP);

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;

    this.#dialog = dialog;
    this.#toggle = this.#findToggle(dialog.id);

    this.#toggle?.addEventListener('click', this.#open);
    // El click en el velo llega con `event.target === dialog`: el `::backdrop`
    // no es un nodo propio. `isClickedOutside` de Horizon tiene el caso
    // especial para eso (`assets/utilities.js:404-414`: para un target
    // HTMLDialogElement compara el punto contra el rect en vez de usar
    // `contains`, que diría que sí siempre).
    dialog.addEventListener('click', this.#onClick);
    // Un solo lugar para el desarmado, y por eso se escucha `close` y no se
    // hace el trabajo en un método `close()`: `Escape` cierra el diálogo por
    // spec sin pasar por ningún handler nuestro. Colgado de `close`, el
    // desbloqueo del scroll y el `aria-expanded` no se pueden desincronizar
    // por una vía de cierre que no hayamos previsto.
    dialog.addEventListener('close', this.#onClose);
    this.#desktop.addEventListener('change', this.#onDesktopChange);

    for (const button of this.querySelectorAll('[data-drawer-close]')) {
      button.addEventListener('click', this.#close);
    }
  }

  disconnectedCallback() {
    const dialog = this.#dialog;

    if (dialog) {
      // Si la sección se re-renderiza (editor de theme) con el drawer abierto,
      // el nodo se va pero `html[scroll-lock]` se queda: la página quedaría
      // sin scroll y sin nada que la desbloquee. Horizon hace lo mismo en
      // `assets/theme-drawer.js:90-91`.
      unlockScroll(dialog);
      dialog.removeEventListener('click', this.#onClick);
      dialog.removeEventListener('close', this.#onClose);
    }

    this.#toggle?.removeEventListener('click', this.#open);
    this.#desktop.removeEventListener('change', this.#onDesktopChange);
  }

  /**
   * Busca el control que abre el drawer por el contrato ARIA que ya declara el
   * header: `aria-controls` apuntando al id del diálogo. Se compara el atributo
   * en JS en vez de armar el selector con el id porque un id con una comilla
   * haría tirar a `querySelector`, y el id sale del DOM.
   *
   * `[data-menu-toggle]` queda como red: si el día que alguien saque el
   * `aria-controls` del header el drawer dejara de abrirse, el síntoma sería
   * "el botón no hace nada", que es de los que se tarda en diagnosticar.
   *
   * @param {string} id - El id del `<dialog>`.
   * @returns {HTMLElement | null}
   */
  #findToggle(id) {
    const byAria = id
      ? [...document.querySelectorAll('[aria-controls]')].find((el) => el.getAttribute('aria-controls') === id)
      : undefined;

    const toggle = byAria ?? document.querySelector('[data-menu-toggle]');

    return toggle instanceof HTMLElement ? toggle : null;
  }

  #open = () => {
    const dialog = this.#dialog;
    // El guard del breakpoint no es defensivo de más: `showModal()` en >=990px
    // dejaría el foco atrapado y el fondo inerte en un panel que el CSS no
    // pinta, sin ninguna hamburguesa a la vista para cerrarlo.
    if (!dialog || dialog.open || this.#desktop.matches) return;

    // Antes de `showModal()`: el diálogo entra en el top layer y el
    // `html[scroll-lock]` de `assets/base.css:36-39` tiene que estar puesto
    // para ese primer fotograma, o la página de atrás salta.
    lockScroll(dialog);
    dialog.showModal();
    this.#toggle?.setAttribute('aria-expanded', 'true');
  };

  #close = () => {
    this.#dialog?.close();
  };

  #onClose = () => {
    const dialog = this.#dialog;
    if (!dialog) return;

    // `unlockScroll` lleva un set de dueños con conteo: si el cart drawer de
    // Horizon también tiene el scroll tomado, esto no se lo suelta.
    unlockScroll(dialog);
    this.#toggle?.setAttribute('aria-expanded', 'false');

    // El `<dialog>` ya devuelve el foco al elemento que lo tenía antes de
    // abrir, pero acá se sabe cuál TIENE que quedar enfocado, así que se
    // afirma. Se saltea cuando el disparador no está renderizado —el caso del
    // cierre por cruzar a >=990px, donde la hamburguesa es `display: none`—
    // porque enfocar un elemento escondido no hace nada y deja el foco en el
    // `<body>` de todas formas.
    const toggle = this.#toggle;
    if (toggle?.isConnected && toggle.offsetParent !== null) toggle.focus();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const dialog = this.#dialog;
    if (dialog && isClickedOutside(event, dialog)) this.#close();
  };

  /** @param {MediaQueryListEvent} event */
  #onDesktopChange = (event) => {
    if (event.matches) this.#close();
  };
}

if (!customElements.get('gamba-menu-drawer')) {
  customElements.define('gamba-menu-drawer', GambaMenuDrawer);
}
